import { createContext, useContext, useState, useEffect } from 'react';

import {
  AppConfigProps,
  PaymentMethod,
  PaymentState,
  UpdatePreferredPaymentBody,
  useCheckoutContext,
} from '@zooplus/checkout-state';

import useMockCheckoutContext from '@tests/StorybookTests/hooks/useCheckoutContext';

import { useTracking } from '@hooks/useTracking';
import { EnhancedPaymentState } from '@types-internal/CheckoutTypes';
import { CreditCardWithPaymentMethod } from '@types-internal/ModernizePaymentTypes';
import { selectConfig } from '@utils/configUtils';
import {
  getInitialPaymentMethods,
  getInitialSelectedPayment,
  trackChangePM,
} from '@utils/modernizePaymentUtils';
import { getEnv } from '@utils/utils';

import { useCreditCardContext } from './CreditCardContextProvider';

export type PaymentContextData = {
  selectedPayment?: PaymentMethod;
  appConfig?: AppConfigProps;
  checkoutState?: EnhancedPaymentState | PaymentState;
  updateSelectedPayment: (data: UpdatePreferredPaymentBody) => void;
  deleteSavedCard: (card: CreditCardWithPaymentMethod) => void;
  setSelectedPayment: (paymentMethod: any) => void;
};
export type PaymentContextProviderProps = {
  isDataMocked?: boolean;
  children?: React.ReactNode;
};
export const PaymentContext = createContext<PaymentContextData>({
  updateSelectedPayment: () => {},
  deleteSavedCard: () => {},
  setSelectedPayment: () => {},
});
export const usePaymentContext = () => useContext(PaymentContext);

export const PaymentContextProvider: React.FC<PaymentContextProviderProps> = ({
  children,
  isDataMocked = false,
}) => {
  const useLoadCheckoutContext = isDataMocked ? useMockCheckoutContext : useCheckoutContext;
  const checkoutContext = useLoadCheckoutContext('payment-frontend');
  const { deselectCreditCard } = useCreditCardContext();

  const { paymentState, appConfig } = checkoutContext ?? {};
  const {
    customerId,
    sid,
    currency,
    customerType,
    deleteSavedCard: deleteSavedCardMutation,
    updatePreferredPayment,
    deleteSavedBancontactCard,
  } = paymentState ?? {};
  const { shop } = appConfig ?? {};
  const { siteId } = shop ?? {};
  const env = getEnv(appConfig?.environment);
  const config = selectConfig(env)(appConfig?.realm);
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>(); // for internal use
  const [checkoutState, setCheckoutState] = useState<PaymentContextData['checkoutState']>(
    getInitialPaymentMethods(paymentState, config.adyen.clientKey),
  );
  const track = useTracking({
    sid,
    siteId,
    currency,
    customerId,
    guest: customerType === 'GUEST_CUSTOMER',
  });

  useEffect(() => {
    const transformedPaymentState = getInitialPaymentMethods(paymentState, config.adyen.clientKey);
    if (!paymentState?.isPaymentLoading) {
      const selectedPM =
        transformedPaymentState?.paymentMethods &&
        getInitialSelectedPayment(
          transformedPaymentState?.paymentMethods,
          transformedPaymentState?.customerType,
          transformedPaymentState?.pciPaymentPageEnabled,
          transformedPaymentState?.preferredPayment?.paymentMethodShortName || '',
          transformedPaymentState?.cartHasAutoshipmentEnabled,
          transformedPaymentState?.cartHasMarketplaceItems,
        );
      setSelectedPayment(selectedPM);
    }
    setCheckoutState(transformedPaymentState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(paymentState)]);

  const updateSelectedPayment = (data: UpdatePreferredPaymentBody) => {
    updatePreferredPayment?.(data);
    trackChangePM(data, checkoutState?.preferredPayment, track);
  };

  const handleSetSelectedPayment = (paymentMethod: PaymentMethod) => {
    setSelectedPayment(paymentMethod);
    deselectCreditCard?.();
  };

  const deleteSavedCard = (cardToDelete: CreditCardWithPaymentMethod) => {
    const { paymentMethod, psp, pspIdentifier } = cardToDelete;

    if (paymentMethod === 'BANCONTACT') {
      if (!pspIdentifier) {
        console.log('Card is missing required PSP identifier for Bancontact');
        return;
      }
      deleteSavedBancontactCard?.({ bancontactIdentifier: pspIdentifier });
    } else {
      if (!psp || !pspIdentifier) {
        console.log('Card is missing required PSP or PSP identifier');
        return;
      }
      deleteSavedCardMutation?.({ psp, pspIdentifier });
    }
  };

  return (
    <PaymentContext.Provider
      value={{
        appConfig,
        selectedPayment,
        checkoutState,
        updateSelectedPayment,
        deleteSavedCard,
        setSelectedPayment: handleSetSelectedPayment,
      }}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContextProvider;
